import {
  CLIENT_EVENT_MAX_BYTES,
  type BpmAction,
  type ClientEvent,
  type ClientEventType,
  clientEventSchema,
  getSerializedSizeBytes,
  parseTelemetryPrimitive,
  safeFieldNames,
  sanitizeError,
  truncateMessage,
} from "./client-event-schema";
import { getAccessTokenOrNull } from "@/lib/auth-store";

type BpmContext = NonNullable<ClientEvent["bpm"]>;
type FormContext = NonNullable<ClientEvent["form"]>;
type HttpContext = NonNullable<ClientEvent["http"]>;
type GlobalEventType = Extract<
  ClientEventType,
  "client.unhandled_error" | "client.unhandled_rejection" | "ui.error_boundary" | "ui.request_error"
>;

interface TelemetrySnapshotObject {
  [key: string]: TelemetrySnapshotValue;
}

type TelemetrySnapshotValue =
  | null
  | string
  | number
  | boolean
  | TelemetrySnapshotObject
  | TelemetrySnapshotValue[];

const CLIENT_EVENT_ENDPOINT = "/api/telemetry/v1/client-events";

type ActionTelemetryInput = {
  eventId?: string;
  action: BpmAction;
  route?: string;
  userEmail?: string | null;
  bpm?: BpmContext;
  form?: FormContext;
  durationMs?: number;
  http?: HttpContext;
  error?: unknown;
};

type ValidationFailureFormContext = Omit<
  FormContext,
  "formValues" | "registeredFields" | "dirtyFields" | "touchedFields" | "errors"
> & {
  formValues: unknown;
  registeredFields: string[];
  dirtyFields: unknown;
  touchedFields: unknown;
  errors: unknown;
};

type ValidationFailureBpmContext = BpmContext & {
  formKey: string;
  processDefinitionKey: string;
};

export type ValidationFailureTelemetryInput = Omit<
  ActionTelemetryInput,
  "bpm" | "form" | "error"
> & {
  bpm: ValidationFailureBpmContext;
  form: ValidationFailureFormContext;
};

type GlobalTelemetryInput = {
  eventId?: string;
  route?: string;
  userEmail?: string | null;
  bpm?: BpmContext;
  digest?: string;
};

function getTodayStamp(): string {
  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

function randomHex8(): string {
  const bytes = new Uint8Array(4);
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    const random = Math.floor(Math.random() * 0xffffffff);
    bytes[0] = (random >>> 24) & 0xff;
    bytes[1] = (random >>> 16) & 0xff;
    bytes[2] = (random >>> 8) & 0xff;
    bytes[3] = random & 0xff;
  }

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function createBpmEventId(): string {
  return `BPM-${getTodayStamp()}-${randomHex8()}`;
}

function getDefaultRoute(): string {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return "server";
  }

  return browserWindow.location.pathname || "/";
}

function getClientBuildSha(): string {
  return import.meta.env.VITE_APP_BUILD_SHA || "local";
}

function normalizeUserEmail(userEmail: string | null | undefined): string | undefined {
  return userEmail ? truncateMessage(userEmail) : undefined;
}

function safeShortString<T>(value: T): string | undefined {
  const text = truncateMessage(value);
  return text && text.length > 250 ? text.slice(0, 250) : text;
}

function safeRoute<T>(route: T): string {
  const text = truncateMessage(route) ?? getDefaultRoute();
  const withoutQuery = text.split(/[?#]/, 1)[0] || "/";
  return withoutQuery.length > 300 ? withoutQuery.slice(0, 300) : withoutQuery;
}

function safeClientBuildSha<T>(clientBuildSha: T): string {
  return safeShortString(clientBuildSha) ?? "local";
}

function serializeValidationSnapshot<T>(
  value: T,
  options: { omitRHFRefs?: boolean } = {},
): TelemetrySnapshotValue {
  return serializeTelemetryValue(
    value,
    new WeakSet<object>(),
    options.omitRHFRefs === true,
  );
}

function isObjectValue<T>(value: T): value is T & object {
  return Object(value) === value;
}

function serializeTelemetryValue<T>(
  value: T,
  ancestors: WeakSet<object>,
  omitRHFRefs: boolean,
): TelemetrySnapshotValue {
  const primitive = parseTelemetryPrimitive(value);
  if (primitive.kind === "null") {
    return null;
  }
  if (primitive.kind === "undefined") {
    return { __telemetryType: "undefined" };
  }
  if (primitive.kind === "number" || primitive.kind === "boolean" || primitive.kind === "text") {
    return primitive.value;
  }

  if (value instanceof Date) {
    return value.toJSON();
  }

  const fileConstructor = globalThis.File;
  if (fileConstructor && value instanceof fileConstructor) {
    return {
      __telemetryType: "File",
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }

  const blobConstructor = globalThis.Blob;
  if (blobConstructor && value instanceof blobConstructor) {
    return {
      __telemetryType: "Blob",
      size: value.size,
      type: value.type,
    };
  }

  if (Object.prototype.toString.call(value) === "[object Function]") {
    return truncateMessage(value) ?? "[object Function]";
  }

  if (!isObjectValue(value)) {
    return truncateMessage(value) ?? "[object Unknown]";
  }

  if (ancestors.has(value)) {
    return { __telemetryType: "circular" };
  }

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => serializeTelemetryValue(item, ancestors, omitRHFRefs));
    }

    if (value instanceof Map) {
      return {
        __telemetryType: "Map",
        entries: Array.from(value, ([key, mapValue]) => [
          serializeTelemetryValue(key, ancestors, omitRHFRefs),
          serializeTelemetryValue(mapValue, ancestors, omitRHFRefs),
        ]),
      };
    }

    if (value instanceof Set) {
      return {
        __telemetryType: "Set",
        values: Array.from(
          value,
          (item) => serializeTelemetryValue(item, ancestors, omitRHFRefs),
        ),
      };
    }

    const result: TelemetrySnapshotObject = {};
    if (value instanceof Error) {
      result.name = serializeTelemetryValue(value.name, ancestors, omitRHFRefs);
      result.message = serializeTelemetryValue(value.message, ancestors, omitRHFRefs);
      result.stack = serializeTelemetryValue(value.stack, ancestors, omitRHFRefs);
    }

    for (const key of Object.keys(value)) {
      if (omitRHFRefs && key === "ref") {
        continue;
      }

      try {
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        const propertyValue = descriptor?.get?.call(value) ?? descriptor?.value;
        result[key] = serializeTelemetryValue(propertyValue, ancestors, omitRHFRefs);
      } catch {
        result[key] = { __telemetryType: "unreadable" };
      }
    }

    return result;
  } finally {
    ancestors.delete(value);
  }
}

function buildMinimalEvent(event: ClientEvent): ClientEvent | null {
  const minimalEvent: ClientEvent = {
    eventId: event.eventId,
    eventType: event.eventType,
    timestamp: event.timestamp,
    action: event.action,
    route: safeRoute(event.route),
    clientBuildSha: safeClientBuildSha(event.clientBuildSha),
    bpm: event.bpm?.taskId
      ? {
          taskId: safeShortString(event.bpm.taskId),
        }
      : undefined,
    error: event.error?.name
      ? {
          name: safeShortString(event.error.name),
        }
      : undefined,
  };

  const parsed = clientEventSchema.safeParse(minimalEvent);
  if (!parsed.success || getSerializedSizeBytes(parsed.data) > CLIENT_EVENT_MAX_BYTES) {
    return null;
  }

  return parsed.data;
}

function normalizeEvent(event: ClientEvent): ClientEvent | null {
  const parsed = clientEventSchema.safeParse(event);
  if (!parsed.success) {
    return buildMinimalEvent(event);
  }

  if (getSerializedSizeBytes(parsed.data) > CLIENT_EVENT_MAX_BYTES) {
    return buildMinimalEvent(parsed.data);
  }

  return parsed.data;
}

function buildEvent({
  eventId,
  eventType,
  action,
  route,
  userEmail,
  bpm,
  form,
  durationMs,
  http,
  error,
}: ActionTelemetryInput & { eventType: ClientEventType }): ClientEvent | null {
  const nextEvent: ClientEvent = {
    eventId: eventId ?? createBpmEventId(),
    eventType,
    timestamp: new Date().toISOString(),
    action,
    route: route ?? getDefaultRoute(),
    clientBuildSha: getClientBuildSha(),
    userEmail: normalizeUserEmail(userEmail),
    bpm,
    form: form
      ? {
          ...form,
          fieldNames: form.fieldNames ? safeFieldNames(form.fieldNames) : undefined,
          fileFieldNames: form.fileFieldNames ? safeFieldNames(form.fileFieldNames) : undefined,
          validationErrorFields: form.validationErrorFields
            ? safeFieldNames(form.validationErrorFields)
            : undefined,
          formValues: form.formValues === undefined
            ? undefined
            : serializeValidationSnapshot(form.formValues),
          registeredFields: form.registeredFields === undefined
            ? undefined
            : form.registeredFields,
          dirtyFields: form.dirtyFields === undefined
            ? undefined
            : serializeValidationSnapshot(form.dirtyFields),
          touchedFields: form.touchedFields === undefined
            ? undefined
            : serializeValidationSnapshot(form.touchedFields),
          errors: form.errors === undefined
            ? undefined
            : serializeValidationSnapshot(form.errors, { omitRHFRefs: true }),
        }
      : undefined,
    durationMs,
    http,
    error: error === undefined ? undefined : sanitizeError(error),
  };

  return normalizeEvent(nextEvent);
}

function buildGlobalEvent<T>(
  eventType: GlobalEventType,
  error: T,
  input: GlobalTelemetryInput = {},
): ClientEvent | null {
  const sanitizedError = {
    ...sanitizeError(error),
    digest: input.digest ? truncateMessage(input.digest) : undefined,
  };
  const nextEvent: ClientEvent = {
    eventId: input.eventId ?? createBpmEventId(),
    eventType,
    timestamp: new Date().toISOString(),
    route: input.route ?? getDefaultRoute(),
    clientBuildSha: getClientBuildSha(),
    userEmail: normalizeUserEmail(input.userEmail),
    bpm: input.bpm,
    error: sanitizedError,
  };

  return normalizeEvent(nextEvent);
}

function sendClientEvent(event: ClientEvent | null, keepalive: boolean): void {
  try {
    const fetchFunction = globalThis.fetch;
    if (!event || !fetchFunction) {
      return;
    }

    const body = JSON.stringify(event);
    if (getSerializedSizeBytes(event) > CLIENT_EVENT_MAX_BYTES) {
      return;
    }

    const accessToken = getAccessTokenOrNull();
    if (!accessToken) return;
    void fetchFunction(CLIENT_EVENT_ENDPOINT, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body,
      keepalive,
    }).catch(() => undefined);
  } catch {
  }
}

export function trackActionStarted(input: ActionTelemetryInput): string {
  const eventId = input.eventId ?? createBpmEventId();
  const event = buildEvent({ ...input, eventId, eventType: "bpm.action.started" });
  sendClientEvent(event, false);
  return eventId;
}

export function trackActionSucceeded(input: ActionTelemetryInput): string {
  const eventId = input.eventId ?? createBpmEventId();
  const event = buildEvent({ ...input, eventId, eventType: "bpm.action.succeeded" });
  sendClientEvent(event, false);
  return eventId;
}

export function trackActionFailed(input: ActionTelemetryInput): string {
  const eventId = input.eventId ?? createBpmEventId();
  const event = buildEvent({ ...input, eventId, eventType: "bpm.action.failed" });
  sendClientEvent(event, true);
  return eventId;
}

export function trackValidationFailed(input: ValidationFailureTelemetryInput): string {
  const eventId = input.eventId ?? createBpmEventId();
  const event = buildEvent({ ...input, eventId, eventType: "bpm.validation.failed" });
  sendClientEvent(event, false);
  return eventId;
}

export function trackClientError<T>(error: T, input?: GlobalTelemetryInput): string {
  const eventId = input?.eventId ?? createBpmEventId();
  const event = buildGlobalEvent("client.unhandled_error", error, { ...input, eventId });
  sendClientEvent(event, true);
  return eventId;
}

export function trackUnhandledRejection<T>(error: T, input?: GlobalTelemetryInput): string {
  const eventId = input?.eventId ?? createBpmEventId();
  const event = buildGlobalEvent("client.unhandled_rejection", error, { ...input, eventId });
  sendClientEvent(event, true);
  return eventId;
}

export function trackBoundaryError<T>(error: T, input?: GlobalTelemetryInput): string {
  const eventId = input?.eventId ?? createBpmEventId();
  const event = buildGlobalEvent("ui.error_boundary", error, { ...input, eventId });
  sendClientEvent(event, true);
  return eventId;
}

export {
  truncateMessage,
};
